import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { sanityClient } from "../../lib/sanity/sanityClient";
import { usePushAutoRegister } from "../../hooks/usePushAutoRegister";
import { getUserTotalXp } from "../../lib/learning/lessons";
import Banner from "../../components/layout/Banner";
import { getCurrentUserStreakSummary } from "../../lib/learning/streaks";
import LevelAccessBlockedModal from "../../components/learning/LevelAccessBlockedModal";
import { checkLevelAccessForCurrentUser } from "../../lib/learning/levelAccess";
import SubscriptionPopup from "../../components/subscriptions/SubscriptionPopup";
import {
  checkPaidJourneyAccessForCurrentUser,
} from "../../lib/access/paidJourneyAccess";
import LevelResumePrompt from "../../components/learning/LevelResumePrompt";
import {
  buildLevelResumePath,
  dismissResumePromptForSession,
  getLatestLevelResumeProgress,
  getLevelResumeProgress,
  isResumePromptDismissed,
} from "../../lib/learning/levelResumeProgress";

const QUEST_ROCK_SRC =
  "https://tknzgegeniiojzsascfl.supabase.co/storage/v1/object/public/pictures/Dashboard%20PNGs/BigRock.png";

const SUBQUEST_ROCK_SRC =
  "https://tknzgegeniiojzsascfl.supabase.co/storage/v1/object/public/pictures/Dashboard%20PNGs/SmallRock.png";

const LOCKED_LEVEL_ROCK_SRC =
  "https://tknzgegeniiojzsascfl.supabase.co/storage/v1/object/public/pictures/Dashboard%20PNGs/RockLock.png";

const COMPLETE_LEVEL_ROCK_SRC =
  "https://tknzgegeniiojzsascfl.supabase.co/storage/v1/object/public/pictures/Dashboard%20PNGs/RockPenguinFoot.png";

const MASCOT_SRC =
  "https://tknzgegeniiojzsascfl.supabase.co/storage/v1/object/public/pictures/Dashboard%20PNGs/Gwin%20Mascot.png";


  const JOURNEY_MASCOT_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Stages/GwinStage%20Thumbnails%20-%20Stage%201Build%20Your%20Money%20Control%20Routine.png";

const JOURNEY_LEVEL_MASCOT_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/Gwin-Run.png";
const QUEST_LEVEL_XP = 40;

const DASHBOARD_BANNER_HEIGHT = 65;
const BOTTOM_BAR_OFFSET = "calc(72px + env(safe-area-inset-bottom, 0px))";
const NEXT_QUEST_CONTENT_HEIGHT = 112;
const MASCOT_SIZE = 110;

const SUBQUESTS_PER_QUEST = {
  1: 3,
  2: 3,
  3: 4,
  4: 3,
  5: 3,
};

const QUEST_META = {
  1: {
    title: "Financial Mindset for Entrepreneurs",
    subquests: {
      1: "Why Finance Matters",
      2: "Habits of Financially Successful Businesses",
      3: "Banking and Credit Habits That Protect the Business",
    },
  },
  2: {
    title: "Introduction to Compliance",
    subquests: {
      1: "Business Compliance Essentials",
      2: "Building a Funder-Ready Folder",
      3: "Document Review and Self-Audit",
    },
  },
  3: {
    title: "Understanding Costs",
    subquests: {
      1: "Traditional vs. Alternative Funding Sources",
      2: "Understanding Funding Costs",
      3: "Building Your Funding Strategy",
      4: "Funding Readiness Assessment",
    },
  },
};

const SUBQUEST_LAYOUT = {
  boulderTop: 5,
  titleTop: 30,
  titleSpacerHeight: 66,
  playfieldMarginTop: 12,
};

function getQuestTitle(qid) {
  return QUEST_META[qid]?.title || `Quest ${qid}`;
}

function getSubquestTitle(qid, sid) {
  return QUEST_META[qid]?.subquests?.[sid] || `Subquest ${sid}`;
}

function parseSubIndex(id) {
  const parts = String(id).split("-");
  const n = Number.parseInt(parts[1], 10);

  return Number.isFinite(n) ? n : 1;
}

function normalizeQuestKey(qid) {
  if (!qid) return "";

  if (qid === "financial-foundations" || qid === "quest-1" || qid === "Q1") {
    return "1";
  }

  if (qid === "quest-2" || qid === "Q2") return "2";
  if (qid === "quest-3" || qid === "Q3") return "3";
  if (qid === "quest-4" || qid === "Q4") return "4";
  if (qid === "quest-5" || qid === "Q5") return "5";

  return String(qid);
}

function normalizeLevelKey(levelKey) {
  if (!levelKey) return "";

  const s = String(levelKey).trim();

  if (/^L\d+$/i.test(s)) return s.toUpperCase();
  if (/^\d+$/.test(s)) return `L${s}`;

  const m = s.match(/(\d+)/);

  return m ? `L${m[1]}` : s.toUpperCase();
}

function parseLevelIndex(levelKey) {
  const m = String(levelKey || "").match(/(\d+)/);
  const n = Number.parseInt(m?.[1] || "", 10);

  return Number.isFinite(n) ? n : 999;
}

function getQuestNumber(value) {
  const normalized = normalizeQuestKey(value);
  const n = Number.parseInt(normalized, 10);

  return Number.isFinite(n) ? n : null;
}

function requiresJourneyAccessCheck(questId) {
  return !!getQuestNumber(questId);
}

function makeLevelKey(questId, subquestId, levelKey) {
  // Match levels using their unique Supabase level key.
  // This is the same approach used by ProgressDashboard.
  return normalizeLevelKey(levelKey);
}

function QuestLockedModal({ open, message, onClose, onBuy }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-2xl">
          🔒
        </div>

        <h2 className="mb-2 text-xl font-extrabold text-gray-900">
          Continue Your Journey
        </h2>

        <p className="mb-5 text-sm leading-relaxed text-gray-600">
          {message ||
            "Subscribe to continue your Gwin journey."}
        </p>

        <button
          type="button"
          onClick={onBuy}
          className="mb-3 w-full rounded-2xl bg-[#FF914D] px-4 py-3 font-bold text-white shadow-md transition hover:bg-[#ff7a1f]"
        >
          Unlock 60-Day Journey
        </button>

        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-2xl border border-orange-200 bg-white px-4 py-3 font-bold text-[#FF914D]"
        >
          Not Now
        </button>
      </div>
    </div>
  );
}

function sortLevelKeys(keys = []) {
  return [...keys].sort(
    (a, b) => parseLevelIndex(a) - parseLevelIndex(b)
  );
}

function sortDashboardLevelKeys(keys = []) {
  return [...keys].sort(
    (a, b) =>
      parseLevelIndex(a) -
      parseLevelIndex(b)
  );
}

function WelcomeHeaderCard({ displayName, compact = false }) {
  const safeName = displayName || "User";

  return (
    <div
      className={[
        "rounded-[28px] border border-[#EFEFEF] bg-white/95 shadow-sm transition-all duration-300",
        compact ? "px-4 py-4" : "px-5 py-5",
      ].join(" ")}
    >
      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#FF914D]">
        Welcome back
      </p>

      <h1
        className={[
          "mt-1 font-black leading-tight tracking-[-0.035em] text-[#333333] transition-all duration-300",
          compact ? "text-[24px]" : "text-[32px]",
        ].join(" ")}
      >
        {safeName}
      </h1>

      <div className="mt-4 rounded-[22px] bg-[#FF914D] px-4 py-3 text-white shadow-[0_12px_24px_rgba(255,145,77,0.22)]">
        <p className="text-[13px] font-bold leading-snug">
          Complete a new level today to build your streak.
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ title, locked }) {
  return (
    <div className="w-full">
      <div className="flex w-full items-center gap-3">
        <div className="h-[1px] flex-1 bg-[#8D8D8D]" />

        <div
          className={[
            "flex min-h-[34px] items-center justify-center rounded-[4px] px-3 py-2 text-center text-[10px] font-bold leading-tight shadow-md sm:text-[11px]",
            locked ? "bg-[#8A8A8A] text-white" : "bg-black text-white",
          ].join(" ")}
          style={{ width: "clamp(126px, 42vw, 152px)" }}
        >
          {title}
        </div>

        <div className="h-[1px] flex-1 bg-[#8D8D8D]" />
      </div>
    </div>
  );
}

function LevelRock({ locked, complete, left, top, onClick }) {
  const rockSrc = locked
    ? LOCKED_LEVEL_ROCK_SRC
    : complete
    ? COMPLETE_LEVEL_ROCK_SRC
    : SUBQUEST_ROCK_SRC;

  const rock = (
    <div className="relative flex items-center justify-center">
      <img
        src={rockSrc}
        alt={
          locked
            ? "Locked level"
            : complete
            ? "Completed level"
            : "Level"
        }
        className="object-contain transition-opacity"
        style={{ width: "clamp(70px, 25vw, 100px)" }}
        draggable="false"
      />
    </div>
  );

  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${left}%`, top: `${top}%` }}
    >
      {locked ? (
        <div className="cursor-default">{rock}</div>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="block transition-opacity hover:opacity-90"
          aria-label="Open level"
        >
          {rock}
        </button>
      )}
    </div>
  );
}

function DecorativeBoulder({
  top = SUBQUEST_LAYOUT.boulderTop,
}) {
  return (
    <div
      className="pointer-events-none absolute left-1/2 z-0 -translate-x-1/2"
      style={{ top: `${top}px` }}
    >
      <img
        src={QUEST_ROCK_SRC}
        alt=""
        className="object-contain"
        style={{ width: "clamp(96px, 32vw, 132px)" }}
      />
    </div>
  );
}

function MascotMarker({ curveRight }) {
  return (
    <div
      className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2"
      style={{
        left: `${curveRight ? 18 : 82}%`,
        top: "45%",
        width: `${MASCOT_SIZE}px`,
        height: `${MASCOT_SIZE}px`,
      }}
    >
      <img
        src={MASCOT_SRC}
        alt="Wyn mascot"
        className="block h-full w-full object-contain"
        draggable="false"
      />
    </div>
  );
}

function SubquestSection({
  subquest,
  sectionIndex,
  isCurrent,
  onLevelClick,
}) {
  const sectionLocked = subquest.levels.every(
    (level) => level.locked
  );

  const curveRight = sectionIndex % 2 === 0;

  const positions = curveRight
    ? [
        { left: 58, top: 31 },
        { left: 68, top: 50 },
        { left: 57, top: 71 },
      ]
    : [
        { left: 42, top: 31 },
        { left: 32, top: 50 },
        { left: 43, top: 71 },
      ];

  return (
    <section
      className="relative mx-auto flex w-full max-w-[380px] flex-col px-2"
      style={{
        minHeight:
          "calc(100svh - 250px - env(safe-area-inset-bottom, 0px))",
      }}
    >
      <DecorativeBoulder top={SUBQUEST_LAYOUT.boulderTop} />

      <div
        aria-hidden="true"
        style={{
          height: `${SUBQUEST_LAYOUT.titleSpacerHeight}px`,
        }}
      />

      <div
        className="absolute inset-x-0 z-10"
        style={{
          top: `${SUBQUEST_LAYOUT.titleTop}px`,
        }}
      >
        <SectionTitle
          title={subquest.title}
          locked={sectionLocked}
        />
      </div>

      <div
        className="relative min-h-[470px] flex-1"
        style={{
          marginTop: `${SUBQUEST_LAYOUT.playfieldMarginTop}px`,
        }}
      >
        {subquest.levels.map((level, idx) => {
          const fallbackTop = Math.min(86, 31 + idx * 18);
          const fallbackLeft = curveRight ? 58 : 42;

          const position = positions[idx] || {
            left: fallbackLeft,
            top: fallbackTop,
          };

          return (
            <LevelRock
              key={level.key}
              locked={level.locked}
              complete={level.complete}
              left={position.left}
              top={position.top}
              onClick={() => onLevelClick(level)}
            />
          );
        })}

        {isCurrent && (
          <MascotMarker curveRight={curveRight} />
        )}
      </div>
    </section>
  );
}

export default function UserDashboard() {
  usePushAutoRegister();

  const navigate = useNavigate();
  const location = useLocation();

const [quests, setQuests] = useState([]);
const [subquests, setSubquests] = useState([]);
const [levelKeysBySubquest, setLevelKeysBySubquest] =
  useState({});
const [levelMetaByKey, setLevelMetaByKey] = useState({});
const [displayName, setDisplayName] = useState("User");

  const [levelStateByKey, setLevelStateByKey] = useState({});
  const [questCompletionById, setQuestCompletionById] =
    useState({});
  const [, setTotalXp] = useState(0);
  const [, setStreakValue] = useState(0);
  const [, setStatsLoading] = useState(true);
  const [selectedQuestId, setSelectedQuestId] =
    useState(null);
  const [headerCompact, setHeaderCompact] = useState(false);
  const [questHeaderBoxed, setQuestHeaderBoxed] =
    useState(false);

  const [showQuestLockedModal, setShowQuestLockedModal] =
    useState(false);
  const [questLockedMessage, setQuestLockedMessage] =
    useState("");
  const [
    showSubscriptionPopup,
    setShowSubscriptionPopup,
  ] = useState(false);
  const [checkingLevelAccess, setCheckingLevelAccess] =
    useState(false);
  const [levelAccessBlock, setLevelAccessBlock] =
    useState(null);
  const [resumePrompt, setResumePrompt] = useState(null);
  const [resumeActionLoading, setResumeActionLoading] =
    useState(false);

  const [dashboardUserId, setDashboardUserId] =
    useState(null);
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});
  const welcomeCardRef = useRef(null);

  useEffect(() => {
  let alive = true;

  async function loadDashboardStructure() {
    try {
      /*
       * FIRST: try the new Sanity levelDraft structure.
       * This is the temporary redesign mapping:
       *
       * Quest: 1
       * Subquest: 1-1
       * Levels: levelDraft documents ordered by levelOrder
       */
      const draftQuery = `
        *[
          _type == "levelDraft" &&
          defined(levelKey)
        ] | order(levelOrder asc) {
          _id,
          title,
          levelKey,
          levelOrder,
          status
        }
      `;

      const levelDrafts = (await sanityClient.fetch(draftQuery)) || [];

      console.log("[Journey] Sanity client config:", sanityClient.config?.());

      console.log("[Journey] Raw levelDrafts from Sanity:", {
        count: levelDrafts.length,
        levelDrafts,
      });

      const usableDrafts = levelDrafts
        .map((level) => ({
          ...level,
          normalizedLevelKey: normalizeLevelKey(level.levelKey),
        }))
        .filter((level) => !!level.normalizedLevelKey);

      if (usableDrafts.length > 0) {
        const draftQuestId = 1;
        const draftSubquestId = "1-1";

        const draftQuests = [
          {
            id: draftQuestId,
            title: "Lesson Redesign Test",
          },
        ];

        const draftSubquests = [
          {
            id: draftSubquestId,
            quest_id: draftQuestId,
            title: "Levels",
          },
        ];

        const draftLevelKeysBySubquest = {
          [draftSubquestId]: sortLevelKeys(
            usableDrafts.map((level) => level.normalizedLevelKey)
          ),
        };

        const draftLevelMetaByKey = {};

        usableDrafts.forEach((level) => {
          const dashboardKey = makeLevelKey(
            draftQuestId,
            draftSubquestId,
            level.normalizedLevelKey
          );

          draftLevelMetaByKey[dashboardKey] = {
            title:
              String(level.title || "").trim() ||
              `Level ${parseLevelIndex(level.normalizedLevelKey)}`,
            status: level.status || "",
            levelOrder: level.levelOrder || null,
          };
        });

        console.log("[Journey] Loaded levelDrafts from Sanity:", {
          count: usableDrafts.length,
          levelKeys: draftLevelKeysBySubquest[draftSubquestId],
          levelMetaByKey: draftLevelMetaByKey,
        });

        if (!alive) return;

        setQuests(draftQuests);
        setSubquests(draftSubquests);
        setLevelKeysBySubquest(draftLevelKeysBySubquest);
        setLevelMetaByKey(draftLevelMetaByKey);

        return;
      }

      /*
       * SECOND: fall back to the old production Sanity level structure.
       */
      const query = `
        *[
          _type == "level" &&
          defined(legacyQuestId) &&
          defined(legacySubquestId) &&
          defined(legacyLevelKey)
        ] | order(order asc) {
          title,
          order,
          legacyQuestId,
          legacySubquestId,
          legacyLevelKey,
          subquest->{
            title,
            order,
            quest->{
              title,
              order
            }
          }
        }
      `;

      const levels = (await sanityClient.fetch(query)) || [];

      const questMap = new Map();
const subquestMap = new Map();
const nextLevelKeysBySubquest = {};
const nextLevelMetaByKey = {};

levels.forEach((level) => {
  const qid = normalizeQuestKey(level.legacyQuestId);
  const sid = String(level.legacySubquestId || "");
  const normalizedLevelKey = normalizeLevelKey(level.legacyLevelKey);

  if (!qid || !sid || !normalizedLevelKey) {
    return;
  }

  if (!questMap.has(qid)) {
    questMap.set(qid, {
      id: Number(qid),
      title:
        level?.subquest?.quest?.title ||
        getQuestTitle(Number(qid)),
    });
  }

  if (!subquestMap.has(sid)) {
    subquestMap.set(sid, {
      id: sid,
      quest_id: Number(qid),
      title:
        level?.subquest?.title ||
        getSubquestTitle(Number(qid), parseSubIndex(sid)),
    });
  }

  if (!nextLevelKeysBySubquest[sid]) {
    nextLevelKeysBySubquest[sid] = [];
  }

  if (!nextLevelKeysBySubquest[sid].includes(normalizedLevelKey)) {
    nextLevelKeysBySubquest[sid].push(normalizedLevelKey);
  }

  const dashboardKey = makeLevelKey(qid, sid, normalizedLevelKey);

  nextLevelMetaByKey[dashboardKey] = {
    title:
      String(level.title || "").trim() ||
      `Level ${parseLevelIndex(normalizedLevelKey)}`,
    order: level.order || null,
  };
});

      const q = Array.from(questMap.values()).sort(
        (a, b) => Number(a.id) - Number(b.id)
      );

      const sq = Array.from(subquestMap.values()).sort((a, b) => {
        const qa = Number(a.quest_id) || 0;
        const qb = Number(b.quest_id) || 0;

        if (qa !== qb) return qa - qb;

        return parseSubIndex(a.id) - parseSubIndex(b.id);
      });

      Object.keys(nextLevelKeysBySubquest).forEach((subId) => {
        nextLevelKeysBySubquest[subId] = sortLevelKeys(
          nextLevelKeysBySubquest[subId]
        );
      });

      if (!alive) return;

      if (!q.length || !sq.length) {
        const questIds = [1, 2, 3];

        const fallbackQuests = questIds.map((qid) => ({
          id: qid,
          title: getQuestTitle(qid),
        }));

        const fallbackSubquests = [];

        questIds.forEach((qid) => {
          const count = SUBQUESTS_PER_QUEST[qid] ?? 3;

          for (let j = 1; j <= count; j += 1) {
            fallbackSubquests.push({
              id: `${qid}-${j}`,
              quest_id: qid,
              title: getSubquestTitle(qid, j),
            });
          }
        });

        setQuests(fallbackQuests);
        setSubquests(fallbackSubquests);
        setLevelKeysBySubquest({});
        setLevelMetaByKey({});

        return;
      }

      setQuests(q);
      setSubquests(sq);
      setLevelKeysBySubquest(nextLevelKeysBySubquest);
      setLevelMetaByKey(nextLevelMetaByKey);
    } catch (error) {
      console.error("Failed to load dashboard structure from Sanity:", error);

      const questIds = [1, 2, 3];

      const fallbackQuests = questIds.map((qid) => ({
        id: qid,
        title: getQuestTitle(qid),
      }));

      const fallbackSubquests = [];

      questIds.forEach((qid) => {
        const count = SUBQUESTS_PER_QUEST[qid] ?? 3;

        for (let j = 1; j <= count; j += 1) {
          fallbackSubquests.push({
            id: `${qid}-${j}`,
            quest_id: qid,
            title: getSubquestTitle(qid, j),
          });
        }
      });

      if (!alive) return;

      setQuests(fallbackQuests);
      setSubquests(fallbackSubquests);
      setLevelKeysBySubquest({});
      setLevelMetaByKey({});
    }
  }

  loadDashboardStructure();

  return () => {
    alive = false;
  };
}, []);


  

  useEffect(() => {
    (async () => {
      try {
        const {
  data: {
    session,
  },
  error: sessionError,
} =
  await supabase.auth.getSession();

if (sessionError) {
  throw sessionError;
}

const user =
  session?.user ||
  null;

        if (!user) {
          setDashboardUserId(null);
          setDisplayName("User");
          return;
        }

        setDashboardUserId(user.id);

        const metaName =
          (
            user.user_metadata?.full_name ||
            ""
          ).trim();

        const emailPart =
          (user.email || "").split("@")[0] ||
          "";

        const prettified = emailPart
          .replace(/[._-]+/g, " ")
          .replace(/\b\w/g, (character) =>
            character.toUpperCase()
          );

        setDisplayName(
          metaName ||
            prettified ||
            "User"
        );

        const {
          data: row,
          error,
        } = await supabase
          .from("user")
          .select("full_name")
          .eq("id", user.id)
          .maybeSingle();

        if (!error) {
          const fullName =
            (row?.full_name || "").trim();

          if (fullName) {
            setDisplayName(fullName);
          }
        }
      } catch {
        // Keep the dashboard fallback name.
      }
    })();
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      setStatsLoading(true);

      const visibleSubquests =
        subquests;

      const visibleQuestIds =
        quests.map((quest) =>
          String(quest.id)
        );

      const availableQuestIds =
        visibleQuestIds;

      const emptyQuestCompletion =
        {};

      visibleQuestIds.forEach(
        (qid) => {
          emptyQuestCompletion[qid] = {
            total: 0,
            done: 0,
            percent: 0,
            complete: false,
          };
        }
      );

      try {
        const uid =
          dashboardUserId;

        const defaultLevelState = {};
        const orderedDashboardLevelKeysSeed =
          [];

        [...visibleSubquests]
          .sort((a, b) => {
            const qa =
              Number(a.quest_id) || 0;

            const qb =
              Number(b.quest_id) || 0;

            if (qa !== qb) {
              return qa - qb;
            }

            return (
              parseSubIndex(a.id) -
              parseSubIndex(b.id)
            );
          })
          .forEach((sub) => {
            const subLevelKeys =
              levelKeysBySubquest[
                sub.id
              ] || [];

            subLevelKeys.forEach(
              (levelKey) => {
                const key =
                  makeLevelKey(
                    sub.quest_id,
                    sub.id,
                    levelKey
                  );

                defaultLevelState[
                  key
                ] = {
                  questId: String(
                    sub.quest_id
                  ),
                  subquestId:
                    String(sub.id),
                  levelKey,
                  total: 0,
                  done: 0,
                  percent: 0,
                  complete: false,
                  unlocked: false,
                };

                orderedDashboardLevelKeysSeed.push(
                  key
                );
              }
            );
          });

        const orderedDashboardLevelKeys =
          sortDashboardLevelKeys(
            orderedDashboardLevelKeysSeed
          );

        if (!uid) {
          const firstAvailableKey =
            orderedDashboardLevelKeys[0];

          if (firstAvailableKey) {
            defaultLevelState[
              firstAvailableKey
            ].unlocked = true;
          }

          if (alive) {
            setLevelStateByKey(
              defaultLevelState
            );

            setQuestCompletionById(
              emptyQuestCompletion
            );

            setTotalXp(0);
            setStreakValue(0);
            setStatsLoading(false);

            if (
              availableQuestIds[0]
            ) {
            setSelectedQuestId(
            (previous) =>
                previous ||
                availableQuestIds[0]
              );
            }
          }

          return;
        }

        let streakSummary = {
          current_streak: 0,
        };

        try {
          streakSummary =
            await getCurrentUserStreakSummary();
        } catch (error) {
          console.warn(
            "Streak summary could not be loaded:",
            error
          );
        }

        const [
          lessonsRes,
          progressRes,
          accurateTotalXp,
        ] = await Promise.all([
          supabase
            .from("lessons")
            .select(
              "id, quest_id, subquest_id, level_key"
            ),

          supabase
            .from("lesson_progress")
            .select(
              "lesson_id, completed, xp_earned"
            )
            .eq("user_id", uid),

          getUserTotalXp(supabase),
        ]);

        const lessons =
          lessonsRes.data || [];

        const progress =
          progressRes.data || [];

        const progressByLessonId =
          new Map(
            progress.map((row) => [
              row.lesson_id,
              row,
            ])
          );

        const groupedLevels =
          new Map();

        lessons.forEach((lesson) => {
          const qid =
            normalizeQuestKey(
              lesson.quest_id
            );

          const sid = String(
            lesson.subquest_id || ""
          );

          const levelKey =
            normalizeLevelKey(
              lesson.level_key
            );

          if (!qid || !sid || !levelKey) {
            return;
          }

          const key = makeLevelKey(
            qid,
            sid,
            levelKey
          );

          

          const progressRow =
            progressByLessonId.get(
              lesson.id
            );

          if (!groupedLevels.has(key)) {
            groupedLevels.set(key, {
              questId: qid,
              subquestId: sid,
              levelKey,
              total: 0,
              done: 0,
              complete: false,
              xpEarned: 0,
            });
          }

          const group =
            groupedLevels.get(key);

          group.total += 1;

          const lessonCompleted = !!progressRow?.completed;

          if (lessonCompleted) {
            group.done += 1;
            group.complete = true;
          }

          group.xpEarned = Math.max(
            group.xpEarned,
            Number(
              progressRow?.xp_earned ||
                0
            )
          );
        });

        const nextLevelState = {};

        orderedDashboardLevelKeys.forEach(
          (key) => {
            const fallback =
              defaultLevelState[key];

            const db =
              groupedLevels.get(key);

            const questId =
              fallback?.questId ||
              db?.questId ||
              "";

            const subquestId =
              fallback?.subquestId ||
              db?.subquestId ||
              "";

            const levelKey =
              fallback?.levelKey ||
              db?.levelKey ||
              "";

            const total =
              db?.total || 0;

            const done =
              db?.done || 0;

            const percent =
              total > 0
                ? Math.round(
                    (done / total) *
                      100
                  )
                : 0;

            const complete =
              total > 0
                ? done >= total
                : !!db?.complete;

            nextLevelState[key] = {
              questId,
              subquestId,
              levelKey,
              total,
              done,
              percent,
              complete,
              unlocked: false,
            };
          }
        );

        let previousComplete = true;

        orderedDashboardLevelKeys.forEach(
          (key, index) => {
            const node =
              nextLevelState[key];

            const isLevel10TestOverride =
  process.env.NODE_ENV === "development" &&
  dashboardUserId ===
    "972a6af1-d782-47cb-bbe7-360616ed4ae6" &&
  String(node.levelKey).toUpperCase() === "L10";

const unlocked =
  index === 0
    ? true
    : previousComplete === true;

node.unlocked =
  unlocked ||
  node.complete ||
  isLevel10TestOverride;

previousComplete = node.complete;

            previousComplete =
              node.complete;
          }
        );

        const questCompletionMap = {
          ...emptyQuestCompletion,
        };

        visibleQuestIds.forEach(
          (qid) => {
            const questLevels =
              orderedDashboardLevelKeys
                .map(
                  (key) =>
                    nextLevelState[
                      key
                    ]
                )
                .filter(
                  (node) =>
                    node.questId ===
                    String(qid)
                );

            const total =
              questLevels.length;

            const done =
              questLevels.filter(
                (node) =>
                  node.complete
              ).length;

            const percent =
              total > 0
                ? Math.round(
                    (done / total) *
                      100
                  )
                : 0;

            questCompletionMap[qid] =
              {
                total,
                done,
                percent,
                complete:
                  total > 0 &&
                  done >= total,
              };
          }
        );

        if (alive) {
          setLevelStateByKey(
            nextLevelState
          );

          setQuestCompletionById(
            questCompletionMap
          );

          setTotalXp(
            Number(
              accurateTotalXp
            ) || 0
          );

          setStreakValue(
            Number(
              streakSummary?.current_streak
            ) || 0
          );

          setStatsLoading(false);

          setSelectedQuestId(
            (previous) => {
              const orderedNodes =
                orderedDashboardLevelKeys
                  .map(
                    (key) =>
                      nextLevelState[
                        key
                      ]
                  )
                  .filter(Boolean);

              const currentLevelNode =
                orderedNodes.find(
                  (node) =>
                    node.unlocked &&
                    !node.complete
                );

              if (
                currentLevelNode?.questId
              ) {
                return String(
                  currentLevelNode.questId
                );
              }

              const firstIncompleteQuestId =
                availableQuestIds.find(
                  (qid) =>
                    !questCompletionMap[
                      qid
                    ]?.complete
                );

              if (
                firstIncompleteQuestId
              ) {
                return String(
                  firstIncompleteQuestId
                );
              }

              const lastQuestId =
                availableQuestIds[
                  availableQuestIds.length -
                    1
                ];

              return lastQuestId
                ? String(lastQuestId)
                : previous || null;
            }
          );
        }
      } catch {
        if (alive) {
          setLevelStateByKey({});

          setQuestCompletionById(
            emptyQuestCompletion
          );

          setTotalXp(0);
          setStreakValue(0);
          setStatsLoading(false);

          setSelectedQuestId(
            (previous) => {
              if (
                previous &&
                availableQuestIds.includes(
                  String(previous)
                )
              ) {
                return String(
                  previous
                );
              }

              return (
                availableQuestIds[0] ||
                null
              );
            }
          );
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [
  quests,
  subquests,
  levelKeysBySubquest,
  dashboardUserId,
]);

  const visibleQuests =
    useMemo(
      () => quests,
      [quests]
    );

  const defaultQuest =
    useMemo(() => {
      if (!visibleQuests.length) {
        return null;
      }

      if (selectedQuestId) {
        const selected =
          visibleQuests.find(
            (quest) =>
              String(quest.id) ===
              String(
                selectedQuestId
              )
          );

        if (selected) {
          return selected;
        }
      }

      const firstIncomplete =
        visibleQuests.find(
          (quest) => {
            const info =
              questCompletionById[
                String(quest.id)
              ];

            return !info?.complete;
          }
        );

      return (
        firstIncomplete ||
        visibleQuests[0]
      );
    }, [
      visibleQuests,
      questCompletionById,
      selectedQuestId,
    ]);

  const currentQuest =
    useMemo(() => {
      if (!defaultQuest) {
        return null;
      }

      return defaultQuest;
    }, [defaultQuest]);

  const currentQuestInfo =
    useMemo(
      () =>
        questCompletionById[
          String(currentQuest?.id)
        ] || {
          total: 0,
          done: 0,
          percent: 0,
          complete: false,
        },
      [
        questCompletionById,
        currentQuest,
      ]
    );

  const nextQuest =
    useMemo(() => {
      if (!currentQuest) {
        return null;
      }

      const index =
        visibleQuests.findIndex(
          (quest) =>
            quest.id ===
            currentQuest.id
        );

      if (
        index < 0 ||
        index >=
          visibleQuests.length - 1
      ) {
        return null;
      }

      return visibleQuests[
        index + 1
      ];
    }, [
      currentQuest,
      visibleQuests,
    ]);

  const currentQuestSubquests =
  useMemo(() => {
    if (!currentQuest) {
      return [];
    }

    return subquests
      .filter(
        (subquest) =>
          Number(subquest.quest_id) ===
          Number(currentQuest.id)
      )
      .sort(
        (a, b) =>
          parseSubIndex(a.id) -
          parseSubIndex(b.id)
      )
      .map((subquest) => {
        const subLevelKeys =
          levelKeysBySubquest[
            subquest.id
          ] || [];

        const levels =
          subLevelKeys.map(
            (levelKey) => {
              const key =
                makeLevelKey(
                  currentQuest.id,
                  subquest.id,
                  levelKey
                );

              const state =
                levelStateByKey[
                  key
                ] || {
                  questId: String(
                    currentQuest.id
                  ),
                  subquestId:
                    String(
                      subquest.id
                    ),
                  levelKey,
                  total: 0,
                  done: 0,
                  percent: 0,
                  complete: false,
                  unlocked: false,
                };

              const levelMeta = levelMetaByKey[key] || {};

              return {
                ...state,
                key,
                title:
                  levelMeta.title ||
                  state.title ||
                  `Level ${parseLevelIndex(levelKey)}`,
                locked:
                  !state.unlocked,
                playLink:
                  `/play/${currentQuest.id}/${subquest.id}/${levelKey}/welcome`,
              };
            }
          );

        return {
          id: subquest.id,
          title: subquest.title,
          link:
            `/quest/${currentQuest.id}/subquest/${subquest.id}`,
          levels,
        };
      });
  }, [
    currentQuest,
    subquests,
    levelKeysBySubquest,
    levelStateByKey,
    levelMetaByKey,
  ]);

  const currentQuestLevels = useMemo(() => {
    return currentQuestSubquests.flatMap((subquest) =>
      subquest.levels.map((level) => ({
        ...level,
        subquestTitle: subquest.title,
        subquestId: subquest.id,
      }))
    );
  }, [currentQuestSubquests]);

  const currentQuestTotalXp =
    useMemo(() => {
      const levelCount =
        currentQuestSubquests.reduce(
          (sum, subquest) =>
            sum +
            subquest.levels.length,
          0
        );

      return (
        levelCount *
        QUEST_LEVEL_XP
      );
    }, [currentQuestSubquests]);

  const currentLevel =
    useMemo(() => {
      const flattened =
        currentQuestSubquests.flatMap(
          (subquest) =>
            subquest.levels.map(
              (level) => ({
                ...level,
                subquestId:
                  subquest.id,
              })
            )
        );

      const firstUnlockedIncomplete =
        flattened.find(
          (level) =>
            !level.locked &&
            !level.complete
        );

      if (
        firstUnlockedIncomplete
      ) {
        return firstUnlockedIncomplete;
      }

      const allComplete =
        flattened.length > 0 &&
        flattened.every(
          (level) =>
            level.complete
        );

      if (allComplete) {
        return flattened[
          flattened.length - 1
        ];
      }

      const firstUnlocked =
        flattened.find(
          (level) =>
            !level.locked
        );

      return (
        firstUnlocked ||
        flattened[0] ||
        null
      );
    }, [currentQuestSubquests]);

  const currentSubquestId =
    currentLevel?.subquestId ||
    null;

  const openLevelWithAccess =
  async (
    level,
    preferredResume = null
  ) => {
    if (
      !level ||
      level.locked ||
      checkingLevelAccess
    ) {
      return false;
    }

    try {
      setCheckingLevelAccess(true);

      const questId =
        level.questId ||
        currentQuest?.id;

      const subquestId =
        level.subquestId;

      const levelKey =
        level.levelKey;

      const questNumber =
        getQuestNumber(questId);

      const levelNumber =
        parseLevelIndex(levelKey);

      const needsPaidAccess =
        requiresJourneyAccessCheck(questId);

      /*
       * Every new learner level now uses the central journey-access check.
       * Active cohort, subscription, referral-month and free-trial access are
       * resolved there. Learning pace is still enforced separately below.
       */
      const paidAccessPromise =
        needsPaidAccess
          ? checkPaidJourneyAccessForCurrentUser({
              questId: questNumber,
              levelNumber,
              userId: dashboardUserId,
            })
          : Promise.resolve({
              allowed: true,
              reason: "free_quest",
              showPayment: false,
            });

      const levelAccessPromise =
  checkLevelAccessForCurrentUser({
    questId,
    subquestId,
    levelKey,

    /*
     * UserDashboard already knows the user.
     * Don't make levelAccess resolve auth again.
     */
    userId:
      dashboardUserId,

    /*
     * Completed levels can reopen immediately.
     */
    targetAlreadyCompletedHint:
      typeof level.complete ===
      "boolean"
        ? level.complete
        : null,
  });

      const resumePromise =
        preferredResume
          ? Promise.resolve(preferredResume)
          : getLevelResumeProgress({
              questId,
              subquestId,
              levelKey,
            });

      const [
        paidAccess,
        access,
        savedResume,
      ] = await Promise.all([
        paidAccessPromise,
        levelAccessPromise,
        resumePromise,
      ]);

      if (!paidAccess.allowed) {
        setQuestLockedMessage(
          paidAccess.message ||
            "Subscribe to continue your Gwin journey."
        );

        setShowQuestLockedModal(true);

        return false;
      }

      if (!access.allowed) {
        setLevelAccessBlock(access);

        return false;
      }

      const fallbackPath =
        level.playLink ||
        `/play/${questId}/${subquestId}/${levelKey}/welcome`;

      const destinationPath =
  savedResume
    ? buildLevelResumePath(
        savedResume
      )
    : fallbackPath;

navigate(
  destinationPath,
  {
    state: {
      /*
       * Tell FlowRunner that UserDashboard already
       * performed the expensive daily/task gate.
       */
      levelAccessPrechecked:
        true,

      levelAccessQuestId:
        normalizeQuestKey(
          questId
        ),

      levelAccessSubquestId:
        String(
          subquestId
        ),

      levelAccessLevelKey:
        normalizeLevelKey(
          levelKey
        ),

      levelAccessCheckedAt:
        Date.now(),
    },
  }
);

      return true;
    } catch (error) {
      console.error(
        "Level access check failed:",
        error
      );

      setLevelAccessBlock({
        allowed: false,
        reason: "access_check_failed",
        title: "We could not check your progress",
        message:
          error?.message ||
          "Please go to your tasks page, refresh, and try again.",
        buttonLabel: "TASKS",
        tasksPath: "/resources?tab=tasks",
      });

      return false;
    } finally {
      setCheckingLevelAccess(false);
    }
  };

  const handleLevelClick =
    async (level) => {
      await openLevelWithAccess(
        level
      );
    };

  const handleResumeContinue =
    async () => {
      const progress =
        resumePrompt;

      if (
        !progress ||
        resumeActionLoading
      ) {
        return;
      }

      setResumeActionLoading(
        true
      );

      setResumePrompt(null);

      try {
        await openLevelWithAccess(
          {
            questId:
              progress.quest_id,
            subquestId:
              progress.subquest_id,
            levelKey:
              progress.level_key,
            locked: false,
            playLink:
              buildLevelResumePath(
                progress
              ),
          },
          progress
        );
      } finally {
        setResumeActionLoading(
          false
        );
      }
    };

  const handleResumeNotNow =
    () => {
      if (resumePrompt?.id) {
        dismissResumePromptForSession(
          resumePrompt.id
        );
      }

      setResumePrompt(null);
    };

  useEffect(() => {
    const node =
      scrollRef.current;

    if (!node) return;

    const onScroll = () => {
      const scrollTop =
        node.scrollTop;

      setHeaderCompact(
        scrollTop > 24
      );

      const welcomeNode =
        welcomeCardRef.current;

      if (welcomeNode) {
        const welcomeBottom =
          welcomeNode.offsetTop +
          welcomeNode.offsetHeight;

        setQuestHeaderBoxed(
          scrollTop +
            DASHBOARD_BANNER_HEIGHT >=
            welcomeBottom - 8
        );
      } else {
        setQuestHeaderBoxed(
          scrollTop > 160
        );
      }
    };

    onScroll();

    node.addEventListener(
      "scroll",
      onScroll,
      {
        passive: true,
      }
    );

    return () =>
      node.removeEventListener(
        "scroll",
        onScroll
      );
  }, []);

  useEffect(() => {
    if (!currentSubquestId) {
      return;
    }

    const node =
      sectionRefs.current[
        currentSubquestId
      ];

    if (!node) return;

    setTimeout(() => {
      node.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  }, [
    currentSubquestId,
    currentQuest?.id,
  ]);

  const hasNextQuest =
    !!nextQuest;

  const nextQuestUnlocked =
    currentQuestInfo.complete &&
    hasNextQuest;

  const nextQuestComingSoon =
    false;

  const nextQuestBarClass =
    nextQuestUnlocked
      ? "bg-black"
      : "bg-[#D5D5DB]";

  const handleNextQuest = () => {
    if (
      !nextQuestUnlocked ||
      !nextQuest
    ) {
      return;
    }

    setSelectedQuestId(
      String(nextQuest.id)
    );

    setTimeout(() => {
      scrollRef.current?.scrollTo(
        {
          top: 0,
          behavior: "smooth",
        }
      );
    }, 50);
  };

  return (
    <div className="flex min-h-screen justify-center bg-[#F4F4F6]">
      <div
        ref={scrollRef}
        className="h-screen w-full max-w-[430px] overflow-y-auto bg-[#F4F4F6]"
        style={{
          paddingBottom:
            "calc(82px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <Banner homeTo="/dashboard" />

        <section className="bg-white px-7 pb-5 pt-5">
          <h1 className="text-[27px] font-black leading-none tracking-[-0.035em] text-[#111111]">
            Hi, {displayName?.split(" ")?.[0] || "User"}
          </h1>
        </section>

        <JourneyStageHero
          currentQuest={currentQuest}
          currentQuestInfo={currentQuestInfo}
        />

        <section className="px-6 pb-8 pt-10">
          <JourneyLevelTimeline
            levels={currentQuestLevels}
            currentLevel={currentLevel}
            checkingLevelAccess={checkingLevelAccess}
            onLevelClick={handleLevelClick}
            nextQuest={nextQuest}
            currentQuestId={currentQuest?.id}
            currentQuestComplete={currentQuestInfo.complete}
          />
        </section>
      </div>

      <LevelResumePrompt
        open={
          !!resumePrompt &&
          !showQuestLockedModal &&
          !showSubscriptionPopup &&
          !levelAccessBlock
        }
        progress={resumePrompt}
        loading={resumeActionLoading || checkingLevelAccess}
        onContinue={handleResumeContinue}
        onNotNow={handleResumeNotNow}
      />

      <QuestLockedModal
        open={showQuestLockedModal}
        message={questLockedMessage}
        onClose={() => setShowQuestLockedModal(false)}
        onBuy={() => {
          setShowQuestLockedModal(false);
          setShowSubscriptionPopup(true);
        }}
      />

      <SubscriptionPopup
        open={showSubscriptionPopup}
        onClose={() => setShowSubscriptionPopup(false)}
      />

      <LevelAccessBlockedModal
        open={!!levelAccessBlock}
        title={levelAccessBlock?.title}
        message={levelAccessBlock?.message}
        buttonLabel={levelAccessBlock?.buttonLabel || "TASKS"}
        onClose={() => setLevelAccessBlock(null)}
        onTasks={() => {
          const tasksPath =
            levelAccessBlock?.tasksPath ||
            "/resources?tab=tasks";

          setLevelAccessBlock(null);
          navigate(tasksPath);
        }}
      />
    </div>
  );
}

function JourneyStageHero({
  currentQuest,
  currentQuestInfo,
}) {
  const stageNumber = Number(currentQuest?.id) || 1;

  const percent = Math.max(
    0,
    Math.min(
      100,
      Number(currentQuestInfo?.percent) || 0
    )
  );

  return (
    <section className="bg-white px-6 pb-7">
      <div className="relative overflow-hidden rounded-[18px]">
        <img
          src={JOURNEY_MASCOT_SRC}
          alt=""
          aria-hidden="true"
          className="block h-auto w-full"
          draggable="false"
        />

        <div className="absolute inset-0">
          <div className="absolute right-3 top-[34px] z-20 w-[63%]">
            <p className="text-[8px] font-black uppercase text-[#111111]">
              Stage {stageNumber}
            </p>

            <h2 className="mt-1 text-[20px] font-black leading-[1.05] tracking-[-0.045em] text-[#111111]">
              {currentQuest?.title || "Your Learning Stage"}
            </h2>

            <p className="mt-2 text-[12px] font-medium leading-snug text-[#222222]">
              Goal: Do 3 short lessons this week
            </p>

            <div className="mt-3 flex gap-3">
              {Array.from({
                length: Math.min(
                  3,
                  Math.max(
                    1,
                    Number(currentQuestInfo?.total) || 3
                  )
                ),
              }).map((_, index) => {
                const done =
                  index < Number(currentQuestInfo?.done);

                return (
                  <div
                    key={index}
                    className={[
                      "h-[7px] flex-1 rounded-full",
                      done
                        ? "bg-[#FF914D]"
                        : "bg-[#777777]",
                    ].join(" ")}
                  />
                );
              })}
            </div>
          </div>

          <div className="absolute bottom-[12px] left-[24px] right-[24px] z-30">
            <div className="h-[8px] overflow-hidden rounded-full bg-white shadow-sm">
              <div
                className="h-full rounded-full bg-[#FF914D] transition-all duration-500"
                style={{
                  width: `${percent}%`,
                }}
              />
            </div>

            <div className="mt-1 flex justify-end">
              <span className="rounded-full bg-white px-2 py-1 text-[7px] font-black text-[#5A5A5A] shadow-sm">
                STAGE COMPLETE {percent}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function JourneyLevelTimeline({
  levels,
  currentLevel,
  checkingLevelAccess,
  onLevelClick,
  nextQuest,
  currentQuestId,
  currentQuestComplete,
}) {
  return (
    <div className="relative">
      <div
        className="absolute bottom-[68px] left-[15px] top-[14px] w-px bg-[#BCBCBC]"
        aria-hidden="true"
      />

      <div className="space-y-3">
        {levels.map((level, index) => {
          const isCurrent =
            currentLevel?.key === level.key;

          return (
            <JourneyLevelRow
              key={level.key}
              level={level}
              index={index}
              current={isCurrent}
              complete={level.complete}
              locked={level.locked}
              checking={checkingLevelAccess}
              onClick={() => onLevelClick(level)}
            />
          );
        })}
      </div>

      <JourneyMilestone
        nextQuest={nextQuest}
        currentQuestId={currentQuestId}
        stageComplete={currentQuestComplete}
      />
    </div>
  );
}

function JourneyLevelRow({
  level,
  current,
  complete,
  locked,
  checking,
  onClick,
}) {
  const levelNumber =
    parseLevelIndex(level.levelKey);

  const title =
    level.title ||
    level.subquestTitle ||
    `Level ${levelNumber}`;

  if (current) {
    return (
      <div className="relative flex items-start gap-4">
        <JourneyTimelineMarker type="current" />

        <div className="min-w-0 flex-1 rounded-[22px] bg-[#F8E5DA] px-5 py-5">
          <span className="text-[8px] font-black uppercase text-[#FF7138]">
            Level {levelNumber}
          </span>

          <div className="mt-1 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="text-[16px] font-black leading-tight text-[#111111]">
                {title}
              </h3>

              <p className="mt-2 text-[8px] font-medium text-[#747474]">
                3–5 min
              </p>

              <button
                type="button"
                onClick={onClick}
                disabled={checking || locked}
                className="mt-3 inline-flex min-h-[34px] items-center justify-center rounded-[7px] bg-[#FF914D] px-4 text-[12px] font-black text-white transition active:scale-[0.98] disabled:opacity-50"
              >
                {checking
                  ? "Checking..."
                  : level.percent > 0
                  ? "Continue"
                  : "Start"}
              </button>
            </div>

            <img
              src={JOURNEY_LEVEL_MASCOT_SRC}
              alt=""
              aria-hidden="true"
              className="h-[82px] w-[82px] shrink-0 object-contain"
              draggable="false"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={locked ? undefined : onClick}
      disabled={locked || checking}
      className={[
        "relative flex w-full items-start gap-4 py-3 text-left",
        locked
          ? "cursor-default"
          : "active:scale-[0.995]",
      ].join(" ")}
    >
      <JourneyTimelineMarker
        type={
          complete
            ? "complete"
            : locked
            ? "locked"
            : "available"
        }
      />

      <div className="min-w-0 flex-1 pt-0.5">
        <p
          className={[
            "text-[7px] font-medium uppercase",
            locked
              ? "text-[#A0A0A0]"
              : "text-[#696969]",
          ].join(" ")}
        >
          Level {levelNumber}
        </p>

        <h3
          className={[
            "mt-0.5 text-[13px] font-black leading-tight",
            locked
              ? "text-[#8D8D8D]"
              : "text-[#111111]",
          ].join(" ")}
        >
          {title}
        </h3>

        <p
          className={[
            "mt-1 text-[7px] font-medium",
            locked
              ? "text-[#AAAAAA]"
              : "text-[#888888]",
          ].join(" ")}
        >
          {complete
            ? "Complete"
            : locked
            ? "Locked"
            : "Available next"}
        </p>
      </div>
    </button>
  );
}

function JourneyTimelineMarker({ type }) {
  if (type === "complete") {
    return (
      <span className="relative z-10 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#7ed957]">
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 fill-none stroke-white"
          strokeWidth="3"
        >
          <path
            d="M5 12.5 9.5 17 19 7.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }

  if (type === "current") {
    return (
      <span className="relative z-10 mt-1 h-[30px] w-[30px] shrink-0 rounded-full bg-[#FF914D]" />
    );
  }

  return (
    <span
      className={[
        "relative z-10 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border bg-[#F4F4F6]",
        type === "locked"
          ? "border-[#C6C6C6]"
          : "border-[#777777]",
      ].join(" ")}
    >
      <span
        className={[
          "text-[12px] font-black",
          type === "locked"
            ? "text-[#AFAFAF]"
            : "text-[#4A4A4A]",
        ].join(" ")}
      >
        🔒
      </span>
    </span>
  );
}

function JourneyMilestone({
  nextQuest,
  currentQuestId,
  stageComplete,
}) {
  const upcomingStage = nextQuest ||
    (Number(currentQuestId) === 1
      ? { id: 2, title: QUEST_META[2].title }
      : null);

  return (
    <div className="relative mt-5 flex items-start gap-4">
      <span className="relative z-10 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-[#BDBDBD] bg-[#F4F4F6]">
        <span className="text-[13px] text-[#9B9B9B]">
          ⚑
        </span>
      </span>

      <div className="min-w-0 flex-1 rounded-[16px] border border-dashed border-[#AAAAAA] px-4 py-3">
        <p className="text-[12px] font-black text-[#777777]">
  {upcomingStage
    ? `Unlock Stage ${upcomingStage.id}`
    : "Stage Complete"}
</p>

        <p className="mt-1 text-[8px] font-medium leading-relaxed text-[#8E8E8E]">
          {upcomingStage
            ? `Unlock Stage ${upcomingStage.id}: ${upcomingStage.title}`
            : "Complete this stage to finish your current learning journey."}
        </p>
      </div>
    </div>
  );
}
